// WishRemindersPanel — per-wish reminders (W35 §3.1).
//
// Per docs/spec/wishlist/W35.2-SUBRESOURCE-UI-POLISH.md §1.2:
// - per-row: channel icon + lead (e.g. "7 天前") + note + fire time + delete
// - add row: inline form (collapsed by default, "+ 新增提醒" trigger)
// - empty state: "還沒有設定提醒。"
//
// Sized for the AGENTS.md §3 #10 sub-component budget (~6KB).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/primitives/ErrorState";
import { useToast } from "@/components/ui/toast";
import {
  createReminder,
  deleteReminder,
  listReminders,
  type WishReminderOut,
} from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";
import { cn } from "@/lib/utils/cn";

export interface WishRemindersPanelProps {
  wishId: string;
  className?: string;
}

const CHANNEL_LABELS: Record<WishReminderOut["channel"], string> = {
  app: "App",
  email: "Email",
  telegram: "Telegram",
};

export function WishRemindersPanel({
  wishId,
  className,
}: WishRemindersPanelProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    remind_at: "",
    lead_unit: "hour" as "day" | "hour",
    lead_value: 1,
    channel: "app" as WishReminderOut["channel"],
    note: "",
  });

  const query = useQuery({
    queryKey: ["wishes", "detail", wishId, "reminders"],
    queryFn: () => listReminders(wishId),
    enabled: Boolean(wishId),
    staleTime: 30_000,
  });

  const create = useMutation({
    mutationFn: () =>
      createReminder(wishId, {
        remind_at: new Date(form.remind_at).toISOString(),
        lead_unit: form.lead_unit,
        lead_value: form.lead_value,
        channel: form.channel,
        note: form.note || null,
      }),
    onSuccess: () => {
      setAdding(false);
      setForm({
        remind_at: "",
        lead_unit: "hour",
        lead_value: 1,
        channel: "app",
        note: "",
      });
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "reminders"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: () =>
      toast.push({ message: "新增提醒失敗", variant: "danger" }),
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteReminder(wishId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "reminders"],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const reminders: WishReminderOut[] = query.data ?? [];

  return (
    <Card
      data-component="WishRemindersPanel"
      data-testid="wish-reminders-panel"
      className={cn("p-s-4", className)}
    >
      <header className="flex items-center justify-between gap-2 mb-s-3">
        <h3 className="font-display text-h3 text-ink-1 flex items-center gap-2">
          <Bell className="h-4 w-4" aria-hidden />
          心願提醒
          <span className="text-meta text-ink-3 font-normal">
            {reminders.length}
          </span>
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setAdding((v) => !v)}
          data-testid="wish-reminders-add-toggle"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          {adding ? "取消" : "新增提醒"}
        </Button>
      </header>
      {adding ? (
        <form
          data-testid="wish-reminders-add-form"
          className="flex flex-col gap-s-2 mb-s-3 p-s-3 bg-paper-deep rounded-md"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <label className="text-meta text-ink-2">
            提醒時間
            <input
              type="datetime-local"
              value={form.remind_at}
              onChange={(e) =>
                setForm((f) => ({ ...f, remind_at: e.target.value }))
              }
              required
              data-testid="wish-reminders-add-time"
              className="w-full mt-1 px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1"
            />
          </label>
          <div className="grid grid-cols-2 gap-s-2">
            <label className="text-meta text-ink-2">
              提前
              <input
                type="number"
                min={0}
                max={365}
                value={form.lead_value}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    lead_value: Number(e.target.value),
                  }))
                }
                data-testid="wish-reminders-add-lead-value"
                className="w-full mt-1 px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1"
              />
            </label>
            <label className="text-meta text-ink-2">
              單位
              <select
                value={form.lead_unit}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    lead_unit: e.target.value as "day" | "hour",
                  }))
                }
                data-testid="wish-reminders-add-lead-unit"
                className="w-full mt-1 px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1"
              >
                <option value="hour">小時</option>
                <option value="day">天</option>
              </select>
            </label>
          </div>
          <label className="text-meta text-ink-2">
            管道
            <select
              value={form.channel}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  channel: e.target.value as WishReminderOut["channel"],
                }))
              }
              data-testid="wish-reminders-add-channel"
              className="w-full mt-1 px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1"
            >
              <option value="app">App 推播</option>
              <option value="email">Email</option>
              <option value="telegram">Telegram</option>
            </select>
          </label>
          <label className="text-meta text-ink-2">
            備註
            <input
              type="text"
              value={form.note}
              onChange={(e) =>
                setForm((f) => ({ ...f, note: e.target.value }))
              }
              maxLength={500}
              data-testid="wish-reminders-add-note"
              className="w-full mt-1 px-s-3 py-s-2 rounded-md border border-hairline bg-paper-elevated text-body-sm text-ink-1"
            />
          </label>
          <Button
            type="submit"
            disabled={create.isPending || !form.remind_at}
            data-testid="wish-reminders-add-submit"
            className="press min-h-11 px-s-4 rounded-full bg-ink-1 text-paper"
          >
            確認新增
          </Button>
        </form>
      ) : null}
      {query.isLoading ? (
        <p className="text-meta text-ink-3">載入中…</p>
      ) : query.isError ? (
        // W54.0 B1 — a failed read is not 「還沒有設定提醒」. The empty state
        // says the person has no reminders; the error state says we could not
        // find out. Only the first one is worth acting on.
        <ErrorState onRetry={() => void query.refetch()} />
      ) : reminders.length === 0 ? (
        <p className="text-meta text-ink-3">還沒有設定提醒。</p>
      ) : (
        <ol className="flex flex-col gap-s-2">
          {reminders.map((r) => (
            <li
              key={r.id}
              data-component="WishReminderRow"
              data-testid={`wish-reminder-row-${r.id}`}
              className="flex items-center gap-2"
            >
              <span className="shrink-0 px-s-2 py-0.5 rounded-full bg-paper-deep text-meta text-ink-2">
                {CHANNEL_LABELS[r.channel]}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-body-sm text-ink-1">
                  {new Date(r.remind_at).toLocaleString("zh-TW")}
                  <span className="text-meta text-ink-3 ml-2">
                    提前 {r.lead_value} {r.lead_unit === "day" ? "天" : "小時"}
                  </span>
                </p>
                {r.note ? (
                  <p className="text-meta text-ink-2 truncate">{r.note}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => del.mutate(r.id)}
                disabled={del.isPending}
                data-testid={`wish-reminder-row-delete-${r.id}`}
                aria-label="刪除提醒"
                className="press shrink-0 min-h-11 min-w-11 flex items-center justify-center rounded-md text-ink-3 hover:bg-paper-deep disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}